import { lazy } from "react";

const POS = lazy(() => import("../Sales/POS/POS"));
const SalesInvoice = lazy(() =>
  import("../Sales/SalesInvoice/SalesInvoice")
);
const SalesInvoiceList = lazy(() =>
  import("../Sales/SalesInvoice/List/SalesInvoiceList")
);
const ViewSalesInvoice = lazy(() =>
  import("../Sales/SalesInvoice/View/ViewSalesInvoice")
);
const SalesInvoiceLegacyRedirect = lazy(() =>
  import("../Sales/SalesInvoice/LegacyRedirect")
);
const SalesOrderList = lazy(() =>
  import("../Sales/SalesOrder/List/SalesOrderList")
);
const SalesOrderForm = lazy(() =>
  import("../Sales/SalesOrder/SalesOrderForm")
);
const ViewSalesOrder = lazy(() =>
  import("../Sales/SalesOrder/View/ViewSalesOrder")
);
const InstallmentList = lazy(() =>
  import("../Sales/Installment/List/InstallmentList")
);
const ViewInstallment = lazy(() =>
  import("../Sales/Installment/View/ViewInstallment")
);
const Status = lazy(() => import("../Sell/Status/Status"));
const Quotes = lazy(() => import("../Sales/POS/QuoteSale/Quotes/Quotes"));
const SalesHistory = lazy(() => import("../Sell/SaleHistory/SalesHistory"));
const SellReturnForm = lazy(() =>
  import("../Sales/SaleReturn/SellReturnForm")
);
const SaleReturns = lazy(() =>
  import("../Sales/SaleReturn/List/SaleReturns")
);
const DirectReturnForm = lazy(() =>
  import("../Sales/DirectReturn/DirectReturnForm")
);
const ViewSellReturn = lazy(() =>
  import("../Sales/SaleReturn/ViewSellReturn")
);
const CloseReg = lazy(() =>
  import("../Sales/POS/Register/CloseReg/CloseReg")
);
// The register-closure detail report, exposed under a POS-scoped route so a
// cashier who can close registers can view the full details without needing
// separate Report access (the report copy lives at /view_register/:id and is
// gated by reportAccess). Same component, POS gate.
const RegisterDetails = lazy(() =>
  import("../Report/RegisterClosures/ViewRegister/ViewRegister")
);
// Counter stock write-off — the cashier-scoped mini Inventory Adjustment covering
// wastage, damaged, expired and lost stock. Lives under Sales, not Inventory,
// because it is a till-side action gated on its own flag; the back-office form
// stays at /inventory_adjustment_form on inventoryAdjustmentAccess.
const StockWastageList = lazy(() =>
  import("../Sales/StockWastage/List/StockWastageList")
);
const StockWastageForm = lazy(() =>
  import("../Sales/StockWastage/Form/StockWastageForm")
);
const CommissionSetup = lazy(() =>
  import("../Sales/Commission/Setup/CommissionSetup")
);
const CommissionReport = lazy(() =>
  import("../Sales/Commission/Report/CommissionReport")
);
// Recurring service billing. Two separate grants: serviceContractAccess for the
// agreements, contractBillingAccess for the queue that invoices them.
const ServiceContracts = lazy(() =>
  import("../Sales/ServiceContract/ContractList/ServiceContracts")
);
const AddEditContract = lazy(() =>
  import("../Sales/ServiceContract/Form/AddEditContract")
);
const ViewContract = lazy(() =>
  import("../Sales/ServiceContract/ViewContract/ViewContract")
);
const ContractBillingRun = lazy(() =>
  import("../Sales/ServiceContract/BillingRun/BillingRun")
);

export const salesRoutes = (access, settings) => [
  { path: "/pos/*", element: POS, title: "pos", enabled: settings?.fiscalYearId && access.posAccess },
  // Direct Invoicing — the back-office invoice desk (cash, credit, partial). The
  // folder, the API route and the access flags keep their salesInvoice* names:
  // only the URL and the words changed, because "Sales Invoice" is also the title
  // printed on every till slip and so said nothing about this screen.
  { path: "/direct_invoicing", element: SalesInvoiceList, title: "directInvoicing", enabled: settings?.fiscalYearId && access.salesInvoiceAccess },
  { path: "/direct_invoicing/new", element: SalesInvoice, title: "newDirectInvoice", enabled: settings?.fiscalYearId && access.salesInvoiceAccess && (access.salesInvoice?.create ?? true) },
  { path: "/direct_invoicing/view/:id", element: ViewSalesInvoice, title: "viewDirectInvoice", enabled: settings?.fiscalYearId && access.salesInvoiceAccess },
  // Same screen as /direct_invoicing/new, hydrated from a POSTED invoice and saving
  // through SalesInvoice/Edit instead of Post. `edit` is the master gate; the five
  // scoped rights decide what is actually editable once inside, and the server
  // re-derives all of it from the stored document (SellEditPolicy).
  { path: "/direct_invoicing/edit/:id", element: SalesInvoice, title: "editDirectInvoice", enabled: settings?.fiscalYearId && access.salesInvoiceAccess && (access.salesInvoice?.edit ?? false) },
  // Same screen as /direct_invoicing/new, hydrated from a Sales Order. Declared
  // after it so the bare route still matches when there is no order id.
  { path: "/direct_invoicing/new/:orderId", element: SalesInvoice, title: "newDirectInvoice", enabled: settings?.fiscalYearId && access.salesInvoiceAccess && (access.salesInvoice?.create ?? true) },
  { path: "/sales_order", element: SalesOrderList, title: "salesOrders", enabled: settings?.fiscalYearId && access.salesOrderAccess },
  { path: "/sales_order/new", element: SalesOrderForm, title: "newSalesOrder", enabled: settings?.fiscalYearId && access.salesOrderAccess && (access.salesOrderNav?.create ?? false) },
  { path: "/sales_order/edit/:id", element: SalesOrderForm, title: "editSalesOrder", enabled: settings?.fiscalYearId && access.salesOrderAccess && (access.salesOrderNav?.edit ?? false) },
  { path: "/sales_order/view/:id", element: ViewSalesOrder, title: "viewSalesOrder", enabled: settings?.fiscalYearId && access.salesOrderAccess },
  // Installment (layaway): the held documents, their payments, collection and cancellation.
  { path: "/installments", element: InstallmentList, title: "installments", enabled: settings?.fiscalYearId && access.installmentAccess },
  { path: "/installments/view/:id", element: ViewInstallment, title: "viewInstallment", enabled: settings?.fiscalYearId && access.installmentAccess },
  // Old URLs kept alive — see LegacyRedirect, which reads the same RENAMED_PATHS
  // table that migrates pinned favourites. One wildcard covers the list, the
  // form and every /view/:id, and each is gated the same way the real routes are
  // so it cannot become a way to probe whether a screen exists.
  { path: "/cash_sales_invoice/*", element: SalesInvoiceLegacyRedirect, title: "directInvoicing", enabled: settings?.fiscalYearId && access.salesInvoiceAccess },
  { path: "/cash_sales_invoice", element: SalesInvoiceLegacyRedirect, title: "directInvoicing", enabled: settings?.fiscalYearId && access.salesInvoiceAccess },
  { path: "/sales_invoice/*", element: SalesInvoiceLegacyRedirect, title: "directInvoicing", enabled: settings?.fiscalYearId && access.salesInvoiceAccess },
  { path: "/sales_invoice", element: SalesInvoiceLegacyRedirect, title: "directInvoicing", enabled: settings?.fiscalYearId && access.salesInvoiceAccess },
  { path: "/status", element: Status, title: "status", enabled: settings?.fiscalYearId && access.status },
  { path: "/quotes", element: Quotes, title: "quotes", enabled: settings?.fiscalYearId && access.pos?.quote },
  // POS Transactions — sales rung up on the till, and nothing else. The server
  // fixes that scope (SellChannel.IsPos); direct invoices are listed at
  // /direct_invoicing and contract invoices on their contract. It was
  // /sales_history, then /all_sales while it still held every channel.
  { path: "/pos_transactions", element: SalesHistory, title: "posTransactions", enabled: settings?.fiscalYearId && access.salesHistoryAccess },
  { path: "/pos_transactions/:customerId", element: SalesHistory, title: "posTransactions", enabled: settings?.fiscalYearId && access.salesHistoryAccess },
  { path: "/all_sales/*", element: SalesInvoiceLegacyRedirect, title: "posTransactions", enabled: settings?.fiscalYearId && access.salesHistoryAccess },
  { path: "/all_sales", element: SalesInvoiceLegacyRedirect, title: "posTransactions", enabled: settings?.fiscalYearId && access.salesHistoryAccess },
  { path: "/sales_history/*", element: SalesInvoiceLegacyRedirect, title: "posTransactions", enabled: settings?.fiscalYearId && access.salesHistoryAccess },
  { path: "/sales_history", element: SalesInvoiceLegacyRedirect, title: "posTransactions", enabled: settings?.fiscalYearId && access.salesHistoryAccess },
  { path: "/sell_return_form/:invoiceId", element: SellReturnForm, title: "saleReturnForm", enabled: settings?.fiscalYearId && access.pos?.returnSale },
  { path: "/sale_returns", element: SaleReturns, title: "saleReturns", enabled: settings?.fiscalYearId && access.pos?.returnSale },
  { path: "/view_sale_return/:id", element: ViewSellReturn, title: "viewSaleReturn", enabled: settings?.fiscalYearId && access.pos?.returnSale },
  { path: "/direct_return", element: DirectReturnForm, title: "directReturn", enabled: settings?.fiscalYearId && access.pos?.returnSale },
  { path: "/close_reg", element: CloseReg, title: "closeRegister", enabled: settings?.fiscalYearId && access.pos?.registerClosing },
  { path: "/register_details/:id", element: RegisterDetails, title: "viewRegister", props: { expandByDefault: true }, enabled: settings?.fiscalYearId && access.pos?.registerClosing },
  { path: "/stock_wastage", element: StockWastageList, title: "stockWastage", enabled: settings?.fiscalYearId && access.wastageAccess },
  { path: "/stock_wastage_form", element: StockWastageForm, title: "newStockWastage", enabled: settings?.fiscalYearId && access.wastageAccess && (access.wastageNav?.create ?? true) },
  { path: "/stock_wastage_form/:id", element: StockWastageForm, title: "stockWastageDetails", enabled: settings?.fiscalYearId && access.wastageAccess },
  { path: "/commission_setup", element: CommissionSetup, title: "commissionSetup", enabled: settings?.fiscalYearId && access.commissionAccessNav?.setup },
  { path: "/commission_report", element: CommissionReport, title: "commissionReport", enabled: settings?.fiscalYearId && access.commissionAccessNav?.report },
  { path: "/service_contracts", element: ServiceContracts, title: "serviceContracts", enabled: settings?.fiscalYearId && access.serviceContractAccess },
  { path: "/service_contract_form", element: AddEditContract, title: "newServiceContract", enabled: settings?.fiscalYearId && access.serviceContractAccess && access.serviceContractAccessNav?.create },
  { path: "/service_contract_form/:id", element: AddEditContract, title: "editServiceContract", enabled: settings?.fiscalYearId && access.serviceContractAccess && access.serviceContractAccessNav?.edit },
  { path: "/service_contract/:id", element: ViewContract, title: "viewServiceContract", enabled: settings?.fiscalYearId && access.serviceContractAccess },
  // Its own grant, not serviceContractAccess — an accountant may invoice the queue
  // without any authority over the agreements themselves.
  { path: "/contract_billing_run", element: ContractBillingRun, title: "contractBillingRun", enabled: settings?.fiscalYearId && access.contractBillingAccess },
];
